import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode
} from 'react';
import settingsApi from '@/apis/settingsApi';

export interface Dimension {
  dimension: string;
  unit: string;
  backendIndex?: number;
}

export interface Settings {
  system_language: string;
  load_dimensions: Dimension[];
}

interface SettingsContextValue {
  settings: Settings | null;
  updateSettings: (s: Settings) => Promise<void>;
  loading: boolean;
  error: string | null;
}


/** Enrich the flat API response with a client-side `backendIndex` per dimension. */
function fromApiResponse(data: Settings): Settings {
  return {
    system_language: data.system_language,
    load_dimensions: (data.load_dimensions ?? []).map((dim, idx) => ({
      ...dim,
      backendIndex: idx,
    })),
  };
}

/** Strip the client-only `backendIndex` before sending to the API. */
function toApiPayload(s: Settings): Pick<Settings, 'system_language'> & {
  load_dimensions: Omit<Dimension, 'backendIndex'>[];
} {
  return {
    system_language: s.system_language,
    load_dimensions: s.load_dimensions.map(({ dimension, unit }) => ({
      dimension,
      unit,
    })),
  };
}


const SettingsContext = createContext<SettingsContextValue>({
  settings: null,
  updateSettings: async () => { },
  loading: true,
  error: null,
});

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch settings from the backend once on mount
  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      try {
        const { data } = await settingsApi.getFrontendSettings();
        if (cancelled) return;

        setSettings(fromApiResponse(data));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch frontend settings:', err);
        setError('Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSettings();
    return () => { cancelled = true; };
  }, []);

  // Persist updated settings to the backend
  const updateSettings = useCallback(async (next: Settings) => {
    const { data } = await settingsApi.updateFrontendSettings(toApiPayload(next));
    setSettings(fromApiResponse(data));
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, loading, error }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
